import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { FiSearch } from "react-icons/fi";

const decodeHtml = (html) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
};

const Navbar = () => {
  const { t } = useTranslation();

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(null);

  const location = useLocation();
  const isHome = location.pathname === "/";

  const searchContainerRef = useRef();

  const toggleMobileMenu = () => setMobileOpen(!mobileOpen);
  const toggleSearch = () => setSearchOpen(!searchOpen);

  const leftMenu = [
    { key: "home", link: "/" },
    { key: "journey", link: "/journey/" },
    {
      key: "gallery",
      link: "/photos/",
      dropdown: [
        { key: "photos", link: "/photos/" },
        { key: "videos", link: "/videos/" },
      ],
    },
  ];

  const rightMenu = [
    {
      key: "blog",
      link: "https://harisharandevgan.com/blog/",
      external: true,
    },
    {
      key: "news",
      link: "/news/",
      dropdown: [
        { key: "news", link: "/news/" },
        { key: "latestEvents", link: "https://harisharandevgan.com/blog/category/latest-event/", external: true },
        { key: "newsClips", link: "/news-clips/" },
        { key: "prKit", link: "/pr-kit/" },
      ],
    },
    { key: "connect", link: "/connect/" },
  ];

  useEffect(() => {
    setMobileOpen(false);
    setMobileDropdownOpen(null);
  }, [location.pathname]);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // WP blog search with debounce
  useEffect(() => {
    if (!searchQuery) return setSearchResults([]);

    const fetchPosts = async () => {
      try {
        const res = await axios.get(
          `https://harisharandevgan.com/blog/wp-json/wp/v2/posts?search=${encodeURIComponent(searchQuery)}&per_page=5`
        );

        setSearchResults(res.data);
      } catch (err) {
        console.error(err);
      }
    };

    const debounce = setTimeout(fetchPosts, 300);

    return () => clearTimeout(debounce);
  }, [searchQuery]);

  // Close search on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setSearchOpen(false);
      }
    };

    if (searchOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [searchOpen]);

  return (
    <header
      className={`fixed w-full top-0 z-50 transition-all duration-300 p-2
      ${isHome
          ? scrolled
            ? "bg-white shadow-md md:bg-white"
            : "bg-white md:bg-blue-600/15 md:backdrop-blur-[2px]"
          : "bg-white shadow-md md:bg-white"
        } border-b-2 border-[#ff6900] ${scrolled ? "md:border-b-2" : "md:border-b-0"
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 md:h-20">

          {/* Mobile Logo */}
          <div className="flex items-center md:hidden">
            <Link to="/">
              <img
                src="/images/hsdlogo.webp"
                alt="Harishrana Devgan"
                className="h-10 sm:h-12 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex flex-1 items-center justify-between">

            {/* Left Menu */}
            <div className="flex justify-start space-x-6 w-[300px]">

              {leftMenu.map((item) =>
                item.dropdown ? (
                  <div
                    key={item.key}
                    className="relative group"
                  >
                    <Link
                      to={item.link}
                      className="text-gray-800 hover:text-blue-600 font-medium whitespace-nowrap flex items-center gap-1"
                    >
                      {t(`menu.${item.key}`)}
                      <span className="text-xs">▼</span>
                    </Link>

                    {/* Desktop Dropdown */}
                    <div className="absolute top-full left-0 mt-2 w-44 bg-white shadow-lg rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 border border-gray-200">

                      {item.dropdown.map((subItem) => (
                        <Link
                          key={subItem.key}
                          to={subItem.link}
                          className="block px-4 py-3 text-[13px] leading-[30px] text-black font-medium uppercase tracking-[5px] hover:bg-yellow-50 hover:text-blue-600 border-b last:border-b-0 [font-family:'Dosis',sans-serif]"
                        >
                          {t(`menu.${subItem.key}`)}
                        </Link>
                      ))}

                    </div>
                  </div>
                ) : (
                  <Link
                    key={item.key}
                    to={item.link}
                    className="text-gray-800 hover:text-blue-600 font-medium whitespace-nowrap"
                  >
                    {t(`menu.${item.key}`)}
                  </Link>
                )
              )}

            </div>

            {/* Center Logo */}
            <div className="flex justify-center flex-shrink-0">
              <Link to="/">
                <img
                  src="/images/hsdlogo.webp"
                  alt="Harishrana Devgan"
                  className="h-16 w-auto"
                />
              </Link>
            </div>

            {/* Right Menu */}
            <div className="flex justify-end space-x-6 w-[300px] items-center relative">

              {rightMenu.map((item) =>
                item.dropdown ? (
                  <div key={item.key} className="relative group">
                    <Link
                      to={item.link}
                      className="text-gray-800 hover:text-blue-600 font-medium whitespace-nowrap flex items-center gap-1"
                    >
                      {t(`menu.${item.key}`)}
                      <span className="text-xs">▼</span>
                    </Link>
                    <div className="absolute top-full right-0 mt-2 w-48 bg-white shadow-lg rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 border border-gray-200">
                      {item.dropdown.map((subItem) =>
                        subItem.external ? (
                          <a
                            key={subItem.key}
                            href={subItem.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block px-4 py-3 text-[13px] leading-[30px] text-black font-medium uppercase tracking-[5px] hover:bg-yellow-50 hover:text-blue-600 border-b last:border-b-0 [font-family:'Dosis',sans-serif]"
                          >
                            {t(`menu.${subItem.key}`)}
                          </a>
                        ) : (
                          <Link
                            key={subItem.key}
                            to={subItem.link}
                            className="block px-4 py-3 text-[13px] leading-[30px] text-black font-medium uppercase tracking-[5px] hover:bg-yellow-50 hover:text-blue-600 border-b last:border-b-0 [font-family:'Dosis',sans-serif]"
                          >
                            {t(`menu.${subItem.key}`)}
                          </Link>
                        )
                      )}
                    </div>
                  </div>
                ) : item.external ? (
                  <a
                    key={item.key}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-800 hover:text-blue-600 font-medium whitespace-nowrap"
                  >
                    {t(`menu.${item.key}`)}
                  </a>
                ) : (
                  <Link
                    key={item.key}
                    to={item.link}
                    className="text-gray-800 hover:text-blue-600 font-medium whitespace-nowrap"
                  >
                    {t(`menu.${item.key}`)}
                  </Link>
                )
              )}

              {/* Search */}
              <div
                className="relative"
                ref={searchContainerRef}
              >
                <button
                  onClick={toggleSearch}
                  aria-label="Open search"
                  className="ml-2 text-gray-800 hover:text-blue-600 focus:outline-none text-xl"
                >
                  <FiSearch />
                </button>

                {searchOpen && (
                  <div className="absolute right-0 top-full mt-2 w-80 md:w-96 bg-white shadow-lg rounded-lg z-50 border border-gray-200">

                    <div className="px-4 py-2 font-semibold text-gray-700 border-b border-gray-200">
                      Posts
                    </div>

                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) =>
                        setSearchQuery(e.target.value)
                      }
                      placeholder={t("search")}
                      className="w-full px-4 py-2 border-b border-gray-200 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      autoFocus
                    />

                    <ul className="max-h-64 overflow-y-auto">
                      {searchResults.length > 0 ? (
                        searchResults.map((post) => (
                          <a
                            key={post.id}
                            href={post.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block px-4 py-3 hover:bg-yellow-50 border-b border-gray-100 transition"
                          >
                            {decodeHtml(post.title.rendered)}
                          </a>
                        ))
                      ) : (
                        searchQuery && (
                          <div className="px-4 py-3 text-gray-500">
                            No results found
                          </div>
                        )
                      )}
                    </ul>

                  </div>
                )}
              </div>

            </div>
          </div>

          {/* Mobile Hamburger */}
          <div className="md:hidden flex items-center">
            <button
              onClick={toggleMobileMenu}
              className="text-black hover:text-blue-600 focus:outline-none text-2xl"
            >
              {mobileOpen ? "✖" : "☰"}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${mobileOpen ? "max-h-screen" : "max-h-0"
          } bg-white`}
      >
        <div className="flex flex-col divide-y divide-gray-200">

          {[...leftMenu, ...rightMenu].map((item) =>
            item.dropdown ? (
              <div
                key={item.key}
                className="border-b border-[#ff6900]"
              >

                {/* Parent Button */}
                <button
                  onClick={() =>
                    setMobileDropdownOpen(
                      mobileDropdownOpen === item.key
                        ? null
                        : item.key
                    )
                  }
                  className="w-full flex items-center justify-between px-6 py-4 text-gray-800 bg-gray-50"
                >
                  <span>{t(`menu.${item.key}`)}</span>

                  <span
                    className={`transition-transform duration-300 ${mobileDropdownOpen === item.key
                        ? "rotate-180"
                        : ""
                      }`}
                  >
                    ▼
                  </span>
                </button>

                {/* Mobile Dropdown */}
                <div
                  className={`overflow-hidden transition-all duration-300 ${mobileDropdownOpen === item.key
                      ? "max-h-60"
                      : "max-h-0"
                    }`}
                >
                  {item.dropdown.map((subItem) =>
                    subItem.external ? (
                      <a
                        key={subItem.key}
                        href={subItem.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          setMobileOpen(false);
                          setMobileDropdownOpen(null);
                        }}
                        className="block pl-10 pr-6 py-3 text-[13px] leading-[30px] text-black font-medium uppercase tracking-[5px] hover:bg-yellow-50 transition-all border-t border-gray-100 [font-family:'Dosis',sans-serif]"
                      >
                        {t(`menu.${subItem.key}`)}
                      </a>
                    ) : (
                      <Link
                        key={subItem.key}
                        to={subItem.link}
                        onClick={() => {
                          setMobileOpen(false);
                          setMobileDropdownOpen(null);
                        }}
                        className="block pl-10 pr-6 py-3 text-[13px] leading-[30px] text-black font-medium uppercase tracking-[5px] hover:bg-yellow-50 transition-all border-t border-gray-100 [font-family:'Dosis',sans-serif]"
                      >
                        {t(`menu.${subItem.key}`)}
                      </Link>
                    )
                  )}
                </div>

              </div>
            ) : (
              <Link
                key={item.key}
                to={item.link}
                onClick={() => setMobileOpen(false)}
                className="px-6 py-4 hover:bg-yellow-50 border-b border-[#ff6900] transition-all"
              >
                {t(`menu.${item.key}`)}
              </Link>
            )
          )}

          {/* Mobile Search */}
          <div className="px-6 py-4 border-t border-gray-200">

            <input
              type="text"
              value={searchQuery}
              onChange={(e) =>
                setSearchQuery(e.target.value)
              }
              placeholder={t("search")}
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />

            {searchResults.length > 0 && (
              <ul className="mt-2 max-h-48 overflow-y-auto border rounded-md">

                {searchResults.map((post) => (
                  <a
                    key={post.id}
                    href={post.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-4 py-3 hover:bg-yellow-50 border-b border-gray-100 transition"
                  >
                    {decodeHtml(post.title.rendered)}
                  </a>
                ))}

              </ul>
            )}

          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
